import { useSetTenderEligibleMutation } from '@shared/api';
import type {
  NcsRow,
  TenderBenchmark,
  TenderCostProfile,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { useAgentPermission } from '@entities/user';
import {
  benchmarkUnitLabel,
  fmtBenchmarkValue,
  fmtMoney,
  fmtPct,
} from '@shared/lib/smetaLabels';
import {
  Muted,
  MutedMargin,
  Row,
  Stack,
  Table,
  TableTitle,
  ThRight,
} from './styled';

interface AnaloguesSectionProps {
  profile: TenderCostProfile | null;
  cohort: TenderCostProfile[];
  benchmarks: TenderBenchmark[];
  ncs: NcsRow[];
}

/** Сравнение с аналогами: профиль самого расчёта + когорта + эталонные полосы.
 *  Когорта/эталоны наполняются по мере курации и импорта НЦС — пока часто
 *  пусты, тогда показываем честную пометку (insufficient_sample). */
export function AnaloguesSection({
  profile,
  cohort,
  benchmarks,
  ncs,
}: AnaloguesSectionProps) {
  const [setEligible, { isLoading: savingEligible }] = useSetTenderEligibleMutation();
  const { can_write } = useAgentPermission('smeta');
  if (!profile && cohort.length === 0 && benchmarks.length === 0 && ncs.length === 0) {
    return (
      <MutedMargin>
        Недостаточно данных для сравнения: профиль расчёта ещё не построен, когорта
        доверенных аналогов и эталоны НЦС пока не накоплены.
      </MutedMargin>
    );
  }
  return (
    <Stack>
      {profile && (
        <MutedMargin>
          Профиль расчёта — базис: {fmtMoney(profile.base_total)} ₽ · текущий:{' '}
          {fmtMoney(profile.current_total)} ₽ · НР: {fmtPct(profile.weighted_nr_pct)} · СП:{' '}
          {fmtPct(profile.weighted_sp_pct)} · позиций: {profile.position_count ?? '—'} (покрыто{' '}
          {profile.covered_positions ?? '—'})
        </MutedMargin>
      )}
      {profile && (
        <Row>
          <Muted>
            {profile.eligible
              ? 'Этот расчёт включён в когорту доверенных аналогов.'
              : 'Этот расчёт не участвует в когорте аналогов.'}
          </Muted>
          <Button
            $variant={profile.eligible ? 'ghost' : 'secondary'}
            $size="sm"
            type="button"
            disabled={savingEligible || !can_write}
            title={!can_write ? 'Только чтение' : undefined}
            onClick={() => setEligible({ id: profile.tender_id, eligible: !profile.eligible })}
          >
            {savingEligible
              ? 'Сохраняю…'
              : profile.eligible
                ? 'Исключить из аналогов'
                : 'Отметить доверенным аналогом'}
          </Button>
        </Row>
      )}
      <Muted>
        Доверенных аналогов в когорте: {cohort.length}.{' '}
        {cohort.length === 0 && 'Отметьте прошлые расчёты доверенными, чтобы наполнить когорту.'}
      </Muted>
      {benchmarks.length === 0 ? (
        <Muted>Эталонные полосы (когорта / НЦС) пока не накоплены.</Muted>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Источник</th>
              <th>Метрика</th>
              <th>Ед.</th>
              <ThRight>P25</ThRight>
              <ThRight>Медиана</ThRight>
              <ThRight>P75</ThRight>
              <ThRight>n</ThRight>
            </tr>
          </thead>
          <tbody>
            {/* Единица у КАЖДОЙ строки своя (метрики в таблице разные), поэтому
                она стоит отдельной колонкой, а не в заголовке, как у соседней
                НЦС. Именно колонкой, а не суффиксом в ячейке: `rub_per_unit` —
                это рубли НА ЕДИНИЦУ МОЩНОСТИ (backend делит итог на
                `capacity_value` и хранит рядом `capacity_unit`), и «48 123,46 ₽»
                в ячейке читалось бы как просто сумма. Форматы значений тоже
                задаёт метрика: доли — количеством, `*_pct` — процентами (теми же,
                что в профиле расчёта выше), рубли — деньгами с копейками. */}
            {benchmarks.map((b) => (
              <tr key={b.id}>
                <td>{b.source === 'ncs' ? 'НЦС' : 'когорта'}</td>
                <td>{b.metric}</td>
                <td>{benchmarkUnitLabel(b.metric, b.capacity_unit)}</td>
                <td className="num">{fmtBenchmarkValue(b.metric, b.p25)}</td>
                <td className="num">{fmtBenchmarkValue(b.metric, b.median)}</td>
                <td className="num">{fmtBenchmarkValue(b.metric, b.p75)}</td>
                <td className="num">{b.sample_size ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {ncs.length > 0 && (
        <>
          <TableTitle>
            Укрупнённый норматив НЦС{' '}
            <Badge $tone="neutral">база МО · {ncs[0].price_level ?? '—'}</Badge>
          </TableTitle>
          <Table>
            <thead>
              <tr>
                <th>Код НЦС</th>
                <th>Наименование</th>
                <th>Ед.</th>
                <ThRight>₽ / ед.</ThRight>
              </tr>
            </thead>
            <tbody>
              {ncs.map((r) => (
                <tr key={r.id}>
                  <td>{r.code}</td>
                  <td>{r.name}</td>
                  <td>{r.measure ?? '—'}</td>
                  <td className="num">{fmtMoney(r.value_rub)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Stack>
  );
}
